import { Button, Form, Image, Input, Select, Space } from "antd";
import { useEffect, useState } from "react";
import AddIcon from "../../assets/icons/AddIcon";
import CheckedIcon from "../../assets/icons/CheckedIcon";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import SelectDropdownLgIcon from "../../assets/icons/SelectDropdownLgIcon";
import {
  useGetEmployeesQuery,
  useUploadPhotoInEmployeeMutation,
} from "../../features/employee/employeeApi";
import { useCreateExpenseMutation } from "../../features/expense/expenseApi";
import { useGetMyVisitListMinimumQuery } from "../../features/visit/visitApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { toast } from "../../lib/toast";
import { getExtension } from "../../utils/string";
import { selectSearchOption } from "../../utils/utils";
import CustomModal from "../ui/CustomModal";
const accept = ".png,.jpg,.jpeg";
type Props = {
  open: boolean;
  handleClose: () => void;
};

const AddExpanseModal = ({ handleClose: parentClose, ...rest }: Props) => {
  const { open, handleClose, handleOpen } = useModal<undefined, "SUCCESS">();
  const [form] = Form.useForm();
  const files = Form.useWatch("files", form);
  const expenseType = Form.useWatch("type", form);
  const employeeID = Form.useWatch("employee_id", form);

  // Component states
  const [fileError, setFileError] = useState<string | null>(null);

  // Queries
  const {
    isLoading: isEmpLoading,
    isError: isEmpError,
    error: empError,
    data: employees,
  } = useGetEmployeesQuery({
    params: { limit: 0, skip: 0, status: "active" },
  });

  const {
    data: visits,
    isLoading: isVisitsLoading,
    isError: isVisitsError,
    error: visitsError,
  } = useGetMyVisitListMinimumQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        status: "completed",
        api_for: "expense",
        expense_type: expenseType,
        business_member_id: employeeID,
      },
    },
    { skip: !expenseType }
  );

  // Mutations
  const [
    uploadFiles,
    { isLoading: isFilesLoading, isError: isFilesError, error: filesError },
  ] = useUploadPhotoInEmployeeMutation();
  const [
    createExpense,
    {
      isLoading: creating,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
    },
  ] = useCreateExpenseMutation();

  const generateFileData = (file: File, fileSize = 5) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    if (fileSize) {
      const MAX_FILE_SIZE = fileSize * 1024 * 1024; // MB in bytes

      if (file?.size > MAX_FILE_SIZE) {
        toast(
          "error",
          `The file size exceeds the ${fileSize}MB limit. Please upload a smaller file`
        );

        return false;
      }
    }

    if (accept && !accept?.includes(getExtension(file.name))) {
      toast(
        "error",
        `Your provided ${getExtension(file.name)} file type not allow`
      );

      return;
    }

    return formData;
  };

  const handleFinish = async (values: any) => {
    const formFiles = values?.files;
    let uploadedFiles = [];
    if (formFiles?.length) {
      try {
        const uploadPromises = formFiles?.map((file: File) => {
          const fileData = generateFileData(file);
          return uploadFiles(fileData).unwrap();
        });

        const uploadResults = await Promise.all(uploadPromises);
        uploadedFiles = uploadResults?.map((f: any) => f?.Location);
      } catch (error) {
        console.log(error);
        toast("error", "File upload failed.");
      }
    }

    if (!isFilesLoading) {
      const selectedEmployee = employees?.data?.employees?.find(
        (e) => e?.id === values?.employee_id
      );

      const expenseData = {
        member_id: selectedEmployee?.member_id,
        business_member_id: values?.employee_id,
        amount: Number(values?.amount),
        remarks: values?.remarks,
        type: values?.type,
        attachments: uploadedFiles,
        visit_id: values?.visit,
        other_name: values?.other_title,
      };
      createExpense({ body: expenseData });
    }
  };

  // Errors
  useError(isEmpError, empError, "Employees get failed.");
  useError(isVisitsError, visitsError, "Visits get failed.");
  useError(isFilesError, filesError, "Files upload failed.");
  useError(isCreateError, createError, "Expense create failed.");

  // Effects
  useEffect(() => {
    if (created) {
      handleOpen({ type: "SUCCESS" });
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);
  return (
    <>
      {/* Success Modal */}
      {open?.type === "SUCCESS" && (
        <CustomModal
          title=""
          footer={null}
          width={400}
          closeIcon={null}
          {...rest}
        >
          <div className="flex justify-center text-center items-center">
            <div className="text-center">
              <div className="flex justify-center text-center items-center">
                <CheckedIcon />
              </div>
              <div className="text-center justify-center text-zinc-700 text-base font-medium py-5">
                Expense added successfully!
              </div>

              <Button
                onClick={() => {
                  handleClose();
                  parentClose();
                }}
                type="primary"
                className="min-w-[100px]"
              >
                Okay
              </Button>
            </div>
          </div>
        </CustomModal>
      )}

      {/* Form */}
      <CustomModal
        title="Add Expense"
        handleClose={parentClose}
        width={450}
        footer={null}
        {...rest}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          requiredMark={false}
        >
          <Form.Item
            className="!mb-4"
            name="employee_id"
            label="Employee Name "
            rules={[
              { required: true, message: "Please select an employee name" },
            ]}
          >
            <Select
              suffixIcon={<SelectDropdownLgIcon />}
              size="large"
              disabled={isEmpLoading}
              showSearch
              filterOption={selectSearchOption}
              options={employees?.data?.employees?.map((e) => ({
                label: `${e?.member?.profile?.name} ${
                  e?.employee_id ? `(${e?.employee_id})` : ""
                }`,
                value: e?.id,
              }))}
              placeholder="Select employee name"
            />
          </Form.Item>
          <Form.Item
            className="!mb-4"
            name="type"
            label="Expense Type"
            // rules={[
            //   { required: true, message: "Please select a expanse type!" },
            // ]}
          >
            <Select
              suffixIcon={<SelectDropdownLgIcon />}
              size="large"
              onChange={() => form.setFieldValue("visit", undefined)}
              options={[
                {
                  label: "Transport",
                  value: "transport",
                },
                {
                  label: "Food",
                  value: "food",
                },
                {
                  label: "Other",
                  value: "other",
                },
              ]}
              placeholder="Select expense type"
            />
          </Form.Item>
          <Form.Item
            className="!mb-4"
            name="visit"
            label="Visit"
            // rules={[{ required: true, message: "Please select a visit!" }]}
          >
            <Select
              suffixIcon={<SelectDropdownLgIcon />}
              size="large"
              disabled={!expenseType || isVisitsLoading}
              options={visits?.data?.visits?.map((v) => ({
                label: v?.title,
                value: v?.id,
              }))}
              placeholder="Select visit"
            />
          </Form.Item>

          {expenseType === "other" ? (
            <Form.Item
              className="!mb-4"
              name="other_title"
              label="Title"
              // rules={[{ required: true, message: "Please enter title!" }]}
            >
              <Input size="large" placeholder="Enter title" />
            </Form.Item>
          ) : null}

          <Form.Item
            className="!mb-4"
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: "Please enter amount" },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve(); // required rule will handle empties
                  }
                  const num = Number(value);
                  if (isNaN(num) || num < 0) {
                    return Promise.reject("Amount must be a positive number");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input size="large" placeholder="Enter amount" />
          </Form.Item>
          <Form.Item
            className="!mb-4"
            name="remarks"
            label="Remarks"
            rules={[{ required: true, message: "Please enter remarks" }]}
          >
            <Input.TextArea placeholder="Write hare..." />
          </Form.Item>

          <Form.Item
            className="!mb-4"
            name="files"
            label="Attachments"
            valuePropName="files"
            getValueFromEvent={(e) => {
              return Array.from(e?.target?.files || []);
            }}
            validateStatus={fileError ? "error" : ""}
            help={fileError || ""}
          >
            {Array.isArray(files) && files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 mb-2.5">
                {files.map((file, idx) => (
                  <div
                    key={idx}
                    className="relative w-[46px] h-[46px] border rounded overflow-hidden group"
                  >
                    <Image
                      width={46}
                      height={46}
                      preview={false}
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="object-cover"
                    />
                    <div
                      className={`absolute left-0 right-0 top-0 bottom-0 w-full h-full bg-black/40 flex items-center justify-center opacity-0 transition-all group-hover:opacity-100`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const newFiles = files.filter((_, i) => i !== idx);
                          form.setFieldValue("files", newFiles);
                        }}
                        className="text-white"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <label
              htmlFor="files"
              className={
                "px-4 w-full flex items-center justify-center gap-2.5 h-[46px] border border-display-medium border-dashed text-display-dark cursor-pointer rounded"
              }
            >
              <AddIcon />
              Upload Attachments
            </label>
            <input
              type="file"
              id="files"
              className="!hidden"
              hidden
              multiple
              accept={accept}
              onChange={(e) => {
                const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
                const selectedFiles = Array.from(e.target.files || []);

                const invalidFiles = selectedFiles.filter(
                  (file) => !allowedTypes.includes(file.type)
                );

                if (invalidFiles.length > 0) {
                  setFileError("Only JPG, JPEG, and PNG files are allowed.");
                } else {
                  setFileError(null);
                  form.setFieldValue("files", [
                    ...(files || []),
                    ...selectedFiles,
                  ]);
                }

                e.target.value = "";
              }}
            />
          </Form.Item>

          <Space size={10}>
            <Button
              htmlType="button"
              className="min-w-[125px] h-10"
              onClick={() => {
                parentClose();
              }}
            >
              Cancel
            </Button>
            <Button
              className="min-w-[125px] h-10"
              loading={creating}
              htmlType="submit"
              type="primary"
            >
              Add Expense
            </Button>
          </Space>
        </Form>
      </CustomModal>
    </>
  );
};

export default AddExpanseModal;
